import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { usePlaidLink } from 'react-plaid-link';
import { useEffect } from 'react';
import { selectLinkToken } from '../../store/selectors/plaidSelectors';
import useApplicationUid from '../../hooks/useApplicationUid';
import { connectACHBankAccount } from '../../store/actions/bankAccount/bankAccountActions';
import { fetchLinkToken } from '../../store/actions/plaid/PlaidActions';
import useStepPost from '../../hooks/useStepPost';
import useApplicationFundingDispatch from '../../hooks/useApplicationFundingDispatch';
import useStandardFundingOptions from '../../hooks/useStandardFundingOptions';
import useBankApplicationFundingDispatch from '../../hooks/useBankApplicationFundingDispatch';
import useAipFlow from '../../hooks/useAipFlow';
import useMetalStreamBankApplicationFundingDispatch from '../../hooks/useMetalStreamBankApplicationFundingDispatch';
import useMetalStreamFundingSourceDispatch from '../../hooks/useMetalStreamFundingSourceDispatch';
import FundingContainer from '../FundingContainer';
import { PAGES } from '../../constants/pages';
import { useGetIsImpersonating } from '../../util/hooks/useGetIsImpersonating';

const FundAccountSelect = ({ onModalClose, handleNext, nextPath }) => {
  const dispatch = useDispatch();
  const linkToken = useSelector(selectLinkToken);
  const { ApplicationUid } = useApplicationUid();
  const history = useHistory();
  const { isSalesAssisted } = useGetIsImpersonating();
  const { onSuccess, onModalNextStep } = useStepPost({
    nextPath,
    handleNext,
    onModalClose,
  });
  const { applicationFundingDispatch } = useApplicationFundingDispatch();
  const {
    bankApplicationFundingDispatch,
  } = useBankApplicationFundingDispatch();
  const {
    metalStreamBankApplicationFundingDispatch,
  } = useMetalStreamBankApplicationFundingDispatch();
  const {
    metalStreamFundingSourceDispatch,
  } = useMetalStreamFundingSourceDispatch();
  const { isAipFlow } = useAipFlow();

  useEffect(() => {
    dispatch(fetchLinkToken({ ApplicationUid }));
  }, [ApplicationUid, dispatch]);

  const connectBankApplicationFunding = (
    bankAccountUid,
    onSuccess,
    onError,
  ) => {
    if (isAipFlow) {
      metalStreamBankApplicationFundingDispatch({
        data: {
          BankAccountUid: bankAccountUid,
        },
        onSuccess: () => {
          if (handleNext) {
            onModalNextStep();
            return;
          }

          onSuccess();
        },
        onError,
      });
      return;
    }

    bankApplicationFundingDispatch({
      data: {
        BankAccountUid: bankAccountUid,
      },
      onSuccess: () => {
        if (handleNext) {
          onModalNextStep();
          return;
        }

        onSuccess(PAGES.FUNDING.NORMAL_FUNDING.AMOUNT.PLAID);
      },
      onError,
    });
  };

  const config = {
    token: linkToken,
    onSuccess: (token, metadata) => {
      const bankAccountIdArray = metadata.accounts.map((account) => account.id);

      const data = {
        PublicToken: token,
        BankName: metadata.institution.name,
        BankAccounts: bankAccountIdArray,
      };

      const onRequestSuccess = (bankAccountUid) => {
        connectBankApplicationFunding(bankAccountUid, () => {
          if (handleNext) {
            onModalNextStep();
            return;
          }

          // On Plaid Success Funding -> If Aip Return null, if Not Aip return Plaid Route
          onSuccess(
            isAipFlow ? null : PAGES.FUNDING.NORMAL_FUNDING.AMOUNT.PLAID,
          );
        });
      };

      dispatch(
        connectACHBankAccount({
          data,
          ApplicationUid,
          onSuccess: onRequestSuccess,
        }),
      );
    },
    onExit: (err) => {
      if (err != null && err.error_code === 'INVALID_LINK_TOKEN') {
        dispatch(fetchLinkToken({ ApplicationUid }));
      }
    },
  };

  const { open } = usePlaidLink(config);

  const openPlaid = () => {
    open();
  };

  useEffect(() => {
    let currentPathname = null;
    history.listen((newLocation, action) => {
      if (action === 'PUSH') {
        if (newLocation.pathname !== currentPathname) {
          currentPathname = newLocation.pathname;
        }
      } else {
        window.location.reload(true);
      }
    });
  }, [history]);

  const useAccountBalanceFunding = () => {
    const data = {
      UseAccountBalance: true,
    };

    metalStreamFundingSourceDispatch({
      data,
      onSuccess,
    });
  };

  const submit = (applicationFundingType, nextRoute) => {
    applicationFundingDispatch({
      data: {
        BankFundingType: applicationFundingType,
      },
      onSuccess: () => {
        if (handleNext) {
          onModalNextStep();
          return;
        }

        onSuccess(nextRoute);
      },
    });
  };

  const options = useStandardFundingOptions(
    openPlaid,
    submit,
    useAccountBalanceFunding,
    isSalesAssisted,
  );

  return (
    <>
      {options.map((option) => (
        <FundingContainer {...option} key={option.fundType} />
      ))}
    </>
  );
};

FundAccountSelect.propTypes = {
  nextPath: PropTypes.string,
  handleNext: PropTypes.func,
  onModalClose: PropTypes.func,
};

export default FundAccountSelect;
